import React from 'react';

import type { GroupDto, Role } from '../../api/types';
import { parseEmblemFromDescription } from '../../domain/groups/groupForm';
import { canEditGroup } from '../../domain/groups/policy';
import { useUsersById } from '../../hooks';
import { AddFighterIcon } from '../../ui/icons/AddFighterIcon';
import { MembersList, RoleBadge } from '../members';

import './GroupCard.css';

export type GroupCardProps = {
  group: GroupDto;
  role?: Role;
  currentUserTelegramId?: string | null;
  onAddMembers?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onConfirm?: () => void;
  onLeave?: () => void;
  onJoin?: () => void;
  onRemoveMember?: (telegramId: number) => void;
};

/**
 * Displays a single group card with leader, members, and actions.
 */
export const GroupCard = React.memo(function GroupCard({
  group,
  role,
  currentUserTelegramId,
  onAddMembers,
  onEdit,
  onDelete,
  onConfirm,
  onLeave,
  onJoin,
  onRemoveMember,
}: GroupCardProps) {
  const canEdit = canEditGroup({
    role,
    group,
    currentUserTelegramId,
  });

  // Check if current user is a member (confirmed or pending)
  const isCurrentUserMember = React.useMemo(() => {
    if (!currentUserTelegramId) return false;
    return group.members.some((m) => m.telegramId === currentUserTelegramId);
  }, [group.members, currentUserTelegramId]);

  // Check if user is the creator
  const isCreator = currentUserTelegramId === group.createdByTelegramId;

  // Show join button if:
  // - Group is OPEN
  // - User is not a member
  // - User is not the creator
  const canJoin = group.visibility === 'open' && !isCurrentUserMember && !isCreator;

  // Collect all user IDs needed (leader + members)
  const neededUserIds = React.useMemo(() => {
    const ids = group.members.map((m) => m.telegramId);
    if (!ids.includes(group.createdByTelegramId)) {
      ids.push(group.createdByTelegramId);
    }
    return ids;
  }, [group.members, group.createdByTelegramId]);

  const { getUser, isLoading: usersLoading } = useUsersById(neededUserIds);

  // Parse emblem from description
  const { emblem, description: cleanDescription } = React.useMemo(
    () => parseEmblemFromDescription(group.description),
    [group.description],
  );

  // Get user display name
  const getUserName = (telegramId: string) => {
    const user = getUser(telegramId);
    if (user) {
      const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      const href = `https://t.me/${user.username}`.trim();
      return <a href={href}>{name || `User ${telegramId}`}</a>;
    }
    return usersLoading ? 'Загрузка...' : `User ${telegramId}`;
  };

  const getUserAvatar = (telegramId: string) => {
    const user = getUser(telegramId);
    const photoUrl = user?.photoUrl;
    const isValidUrl = photoUrl && (photoUrl.startsWith('http') || photoUrl.startsWith('/'));
    if (isValidUrl) {
      return <img src={photoUrl} alt="" />;
    }
    const initial = user?.firstName?.[0]?.toUpperCase() || '?';
    return <span className="member-avatar-placeholder">{initial}</span>;
  };

  const getUserRole = (telegramId: string) => {
    const user = getUser(telegramId);
    if (!user) return null;
    return <RoleBadge role={user.role} hasClubKey={user.hasClubKey} />;
  };

  return (
    <div className="group-card">
      <div className="group-card-header">
        <span className="group-emblem">{emblem}</span>
        <div className="group-card-info">
          <div className="group-card-title-row">
            <h3 className="group-card-title">{group.name}</h3>
            <span className={`group-visibility-badge group-visibility-${group.visibility}`}>
              {group.visibility === 'open' ? '🌍 Открытая' : '🔒 Приватная'}
            </span>
            {canJoin && onJoin && (
              <button
                type="button"
                className="group-join-badge-btn"
                onClick={onJoin}
                aria-label="Вступить в группу"
              >
                <span className="join-icon">+</span> Вступить
              </button>
            )}
          </div>
          <div className="group-card-member-count">
            <span aria-hidden="true">👥</span>
            <span className="group-card-member-count-number">{group.members.length}</span>
            <span className="group-card-member-count-text">участников</span>
          </div>
        </div>
        {canEdit && (
          <div className="group-card-actions">
            {onAddMembers && (
              <button
                type="button"
                className="group-invite-btn"
                onClick={onAddMembers}
                aria-label="Добавить участников"
              >
                <AddFighterIcon width="24" height="24" />
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                className="group-edit-btn"
                onClick={onEdit}
                aria-label="Редактировать"
              >
                ✎
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="group-delete-btn"
                onClick={onDelete}
                aria-label="Удалить"
              >
                🗑
              </button>
            )}
          </div>
        )}
      </div>

      <div className="group-card-bodywrap">
        <div className="group-card-body">
          <div className="group-card-col">
            {/* Leader section */}
            <div className="group-card-leader-section">
              <div className="leader-label">Лидер отряда</div>
              <div className="leader-item">
                <span className="leader-icon">👑</span>
                <span className="leader-name">{getUserName(group.createdByTelegramId)}</span>
              </div>
            </div>

            {cleanDescription && (
              <div className="group-card-description-section">
                <div className="description-label">О группе</div>
                <div className="description-content">
                  <span className="description-icon">{'\uD83D\uDCD6'}</span>
                  <span className="description-text">{cleanDescription}</span>
                </div>
              </div>
            )}
          </div>

          <div className="group-card-col">
            <MembersList
              members={group.members}
              currentUserTelegramId={currentUserTelegramId ?? ''}
              canEdit={canEdit}
              renderName={getUserName}
              renderAvatar={getUserAvatar}
              renderRole={getUserRole}
              onConfirm={onConfirm}
              onLeave={onLeave}
              onRemove={onRemoveMember}
              headerLabel="Участники отряда"
              collapsible={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
